import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Badge from "react-bootstrap/Badge";
import { IdwTable } from "@containers/common/idw-table/idw-table";
import { PaginationContainer } from "@containers/common/pagination-container/pagination-container";
import { TableCellWithButton } from "@containers/common/table-cell-with-button/table-cell-with-button";
import { IdwTableContainer } from "@containers/common/idw-table-container/idw-table-container";
import { resources } from "@constants/hardcoded-strings";
import { MANAGEMENT_CONSOLE_MASTER_DATA_WORKFLOW_TEMPLATES_DETAILS_URL } from "@constants/routes";
import { EntityStatusTypes } from "@enums/entity-status-types";
import { ButtonSizeTypes } from "@enums/button-size-types";
import { TableSettingsTableTypes } from "@enums/table-settings-table-types";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import { useTableFilters } from "@hooks/use-table-filters";
import { FormattedWorkflowTemplate } from "@idw-types/data/reference-data/generic";
import {
    createColumnHelper,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    TableState,
    useReactTable
} from "@tanstack/react-table";
import { navigateToUtil } from "@utils/navigation-util";
import { injectWorkflowTemplateId } from "@utils/parameter-injector";
import { sortStrings } from "@utils/string-util";
import { useDispatch } from "react-redux";
import styles from "./workflow-templates-table-container.less";
import { handleSetColumnFiltersChange, handleSetPaginationChange, handleSetSortingChange } from "@utils/react-table/react-table-util";
import { ENTITY_STATUS_OPTIONS } from "@constants/entity-status-options";

type WorkflowTemplatesTableContainerProps = {
    workflowTemplates: Array<FormattedWorkflowTemplate>;
    tableState: TableState;
    isPageHeight: boolean;
};

type WorkflowTemplatesTableDataRow = FormattedWorkflowTemplate & {
    organizationName: string;
};

export const WorkflowTemplatesTableContainer = (
    props: WorkflowTemplatesTableContainerProps
) => {
    const navigate = useNavigate();
    const routerLocation = useLocation();
    const dispatch = useDispatch();

    const { workflowTemplates, tableState, isPageHeight } = props;

    const handleViewOnClick = (workflowTemplate: WorkflowTemplatesTableDataRow) => {
        const url = injectWorkflowTemplateId(
            MANAGEMENT_CONSOLE_MASTER_DATA_WORKFLOW_TEMPLATES_DETAILS_URL,
            workflowTemplate.id
        );
        navigateToUtil(url, navigate, routerLocation.pathname);
    };

    const workflowTemplatesTableData: Array<WorkflowTemplatesTableDataRow> = useMemo(() => {
        return workflowTemplates.map(wt => ({
            ...wt,
            organizationName: wt.organization?.name || resources.common.notFound
        }));
    }, [workflowTemplates]);

    const columnHelper = createColumnHelper<WorkflowTemplatesTableDataRow>();
    const columns = useMemo(() => ([
        columnHelper.accessor("name", {
            header: resources.tables.workflowTemplates.columns.name.header,
            filterFn: "fuzzyText",
            cell: (props) => (
                <TableCellWithButton
                    id="view-workflow-template-details-button"
                    size={ButtonSizeTypes.SMALL}
                    variant={"outline-primary"}
                    icon={faEye}
                    text={props.row.original.name}
                    originalObject={props.row.original}
                    onClick={handleViewOnClick}
                />
            ),
            sortingFn: (a, b) => sortStrings(a.original.name, b.original.name)
        }),
        columnHelper.accessor("id", {
            header: resources.tables.workflowTemplates.columns.id.header,
            filterFn: "fuzzyText"
        }),
        columnHelper.accessor("organizationName", {
            header: resources.tables.verificationServices.columns.organizationName.header,
            filterFn: "fuzzyText",
            sortingFn: (a, b) => sortStrings(a.original.organizationName, b.original.organizationName)
        }),
        columnHelper.accessor("status", {
            header: resources.tables.workflowTemplates.columns.status.header,
            cell: (props) => {
                switch (props.getValue()) {
                    case EntityStatusTypes.ACTIVE:
                        return <Badge bg={"success"}>{resources.common.active}</Badge>;
                    case EntityStatusTypes.INACTIVE:
                        return <Badge bg={"danger"}>{resources.common.inactive}</Badge>;
                    default:
                        return <span>{resources.common.notFound}</span>;
                }
            },
            filterFn: "singleSelectFilter",
            meta: {
                filterOptions: ENTITY_STATUS_OPTIONS
            },
        })
    ]), [workflowTemplatesTableData]);

    const { filterTypes } = useTableFilters();

    const useTableResult = useReactTable({
        columns,
        data: workflowTemplatesTableData,
        state: tableState,
        filterFns: filterTypes,
        sortDescFirst: false,
        autoResetPageIndex: true,
        onSortingChange: (sortingOrUpdateFn) => handleSetSortingChange(sortingOrUpdateFn, TableSettingsTableTypes.WORKFLOW_TEMPLATES, tableState.sorting, dispatch),
        onColumnFiltersChange: (filtersOrUpdateFn) => handleSetColumnFiltersChange(filtersOrUpdateFn, TableSettingsTableTypes.WORKFLOW_TEMPLATES, tableState.columnFilters, dispatch),
        onPaginationChange: (paginationOrUpdateFn) => handleSetPaginationChange(paginationOrUpdateFn, TableSettingsTableTypes.WORKFLOW_TEMPLATES, tableState.pagination, dispatch),
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel()
    });

    return (
        <IdwTableContainer className={styles.workflowTemplatesTableContainer}>
            <IdwTable
                id="workflow-templates"
                useTableResult={useTableResult}
                headerColumnKeyPrefix={"workflow-templates"}
                className={styles.workflowTemplatesTable}
            />
            <PaginationContainer
                idPrefix="workflow-templates"
                useTableResult={useTableResult}
                isPageHeight={isPageHeight}
            />
        </IdwTableContainer>
    );
};